import * as React from "react"
import { Accordion, Box, Flex, List } from "@chakra-ui/react"
import styled from "@emotion/styled"
import { User } from "./_user"
import { UserModeratorView } from "./_user_modview"
import {
  userListSelector,
  localUserIdAtom,
  usersSelector
} from "../../../recoil/atoms/users"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { ModerationSubTitle } from "../moderation/moderation.styled"
import { brandingSelector, roomSelector } from "../../../recoil/atoms/room"
import { ElementTitle } from "../../common/text/element-title"
import NotificationSound from "../../../assets/sounds/notificationNewUsers/newUser.wav"
import { notificationNewUsersSelector } from "../../../recoil/atoms/notification"
import { useLocalUserType } from "../../../hooks/useLocalUserType"
import { audienceTotalCountSelector } from "../../../recoil/atoms/userCounts"
import { ViewportList } from "react-viewport-list"
import { useTranslation } from "react-i18next"
import type { Room } from "../../../recoil/atoms/room/types"
import layoutStates from "../../../recoil/atoms/layout"

const UserType = window.MeetOnline?.UserType

interface UserObject {
  id: string
}

interface UserListProps {
  display?: string
}

interface UsersListsProps {
  type: string
  list: UserObject[]
}

const AudienceListHeader = ({ t }) => {
  const audienceCount = useRecoilValue(audienceTotalCountSelector)
  return (
    <>
      {t("Audience")} ({audienceCount})
    </>
  )
}
interface StyledProps {
  room?: Room
}

const Container = styled(List)<StyledProps>`
  &::-webkit-scrollbar-track {
    background: ${({ roomStyles }) =>
      roomStyles.servicePanelElementBorder || "#cbd5e0"};
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ roomStyles }) =>
      roomStyles.servicePanelElementBorderOffset || "#fff"};
    border-radius: 10px;
  }
`

const _UserList = (props: UserListProps) => {
  const { t } = useTranslation()
  const roomStyles = useRecoilValue(brandingSelector)
  const room = useRecoilValue(roomSelector)
  const { isAudience, isModerator } = useLocalUserType()
  const _localUserId = useRecoilValue(localUserIdAtom)
  const _localUser = useRecoilValue(usersSelector(_localUserId))
  const isSidePanelWithSingleTab = useRecoilValue(
    layoutStates.isSidePanelWithSingleTab
  )

  const { isSoundNewUser, isEnabledNotificationNewUsers } = useRecoilValue(
    notificationNewUsersSelector
  )
  const setNotificationParams = useSetRecoilState(notificationNewUsersSelector)

  const handlePlayNotification = React.useCallback(() => {
    const notificationPlayer = document.getElementById(
      "notification-new-users-player"
    ) as HTMLAudioElement
    notificationPlayer.volume = 0.4
    notificationPlayer.play()
    setNotificationParams({ isSoundNewUser: false })
  }, [setNotificationParams])

  React.useEffect(() => {
    if (isEnabledNotificationNewUsers && isSoundNewUser) {
      handlePlayNotification()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabledNotificationNewUsers, isSoundNewUser])

  const userList = useRecoilValue(userListSelector)

  const moderatorList = React.useMemo<UserObject[]>(
    () =>
      userList.filter(
        user => user?.type === UserType?.MODERATOR && user!.id !== "101"
      ),
    [userList]
  )
  const brocasterList = React.useMemo<UserObject[]>(
    () =>
      userList.filter(
        user => user?.type === UserType?.BROADCASTER && user!.id !== "101"
      ),
    [userList]
  )
  const audienceList = React.useMemo<UserObject[]>(
    () =>
      userList.filter(
        user => user?.type === UserType?.AUDIENCE && user!.id !== "101"
      ),
    [userList]
  )

  const presentersList = React.useMemo<UserObject[]>(
    () =>
      userList.filter(
        user => user?.type !== UserType?.AUDIENCE && user!.id !== "101"
      ),
    [userList]
  )

  const usersLists = React.useMemo<UsersListsProps[]>(
    () => [
      { type: t("Moderators"), list: moderatorList },
      { type: t("Broadcasters"), list: brocasterList }
      /* { type: "AUDIENCE", list: audienceList } */
    ],
    [brocasterList, moderatorList, t]
  )

  const usersListsForAudienceUser = React.useMemo<UsersListsProps[]>(
    () => [
      { type: t("Presenters"), list: presentersList }
      /* { type: "AUDIENCE", list: audienceList } */
    ],
    [presentersList, t]
  )

  const ref = React.useRef(null)

  return (
    <Box height="100%" overflowX="hidden" overflowY="hidden">
      <Box p="1rem 1rem 0rem 1rem" w="100%">
        {/* Room Name Header */}
        <Flex
          padding="0.3rem 0.6rem 0.3rem 0.6rem"
          border={
            roomStyles?.servicePanelElementBorder
              ? `2px solid ${roomStyles?.servicePanelElementBorder}`
              : "2px solid #ced4da"
          }
          borderRadius="0.3rem"
          bg={roomStyles?.servicePanelElementBackground ?? "white"}
          justifyContent="center"
          alignItems="center"
          textAlign="center"
        >
          <ElementTitle text={room.name} marginBottom="0rem" />
        </Flex>
      </Box>

      <Box
        p="0.5rem 0.5rem 1rem 0rem"
        height="calc(100% - 60px)"
        overflowX="hidden"
        overflowY="hidden"
        paddingBottom={isSidePanelWithSingleTab ? "70px" : undefined} // 70px to make same as chat list
      >
        <Container
          roomStyles={roomStyles}
          overflowX="hidden"
          w="100%"
          p="0rem 0.5rem 0 1rem"
          overflowY="auto"
          scrollbarwidth="thin"
          style={{ scrollBehavior: "smooth" }}
          // spacing={4}
          h="100%"
        >
          <div>
            <audio id="notification-new-users-player" src={NotificationSound} />
          </div>

          {_localUser?.type === UserType.MODERATOR ? (
            <>
              {usersLists.map(item => (
                <React.Fragment key={`userTypeOuter-${item.type}`}>
                  {item.list.length !== 0 && (
                    <Box key={`userType-${item.type}`} marginBottom="0.6rem">
                      <ModerationSubTitle
                        style={{ marginBottom: "0.4rem" }}
                        roomStyles={roomStyles}
                        key={`title-${item.type}`}
                      >
                        {item.type} ({`${item.list.length}`})
                      </ModerationSubTitle>
                      <Accordion spacing={0.5} allowToggle>
                        {item.list.map((userObj, arrIndex, sortedList) => (
                          <UserModeratorView
                            userId={userObj.id}
                            key={`userId-${userObj.id}`}
                            isLast={arrIndex === sortedList.length - 1}
                            isModerator={isModerator}
                            isAudience={isAudience}
                          />
                        ))}
                      </Accordion>{" "}
                    </Box>
                  )}
                </React.Fragment>
              ))}
              {audienceList.length > 0 && (
                <Box marginBottom="0.6rem" key={`item.type`}>
                  <ModerationSubTitle
                    style={{ marginBottom: "0.4rem" }}
                    roomStyles={roomStyles}
                  >
                    <AudienceListHeader t={t} />
                  </ModerationSubTitle>
                  <Accordion spacing={0.5} allowToggle ref={ref}>
                    <ViewportList
                      viewportRef={ref}
                      items={audienceList}
                      itemSize={42}
                    >
                      {(user, index) => (
                        <UserModeratorView
                          userId={user.id}
                          key={user.id}
                          isLast={false}
                          isAudience={isAudience}
                          isModerator={isModerator}
                        />
                      )}
                    </ViewportList>
                  </Accordion>
                </Box>
              )}
            </>
          ) : _localUser?.type === UserType.BROADCASTER ? (
            <>
              {usersLists.map(item => (
                <React.Fragment key={`userTypeOuter-${item.type}`}>
                  {item.list.length !== 0 && (
                    <Box key={`userType-${item.type}`} marginBottom="0.6rem">
                      <ModerationSubTitle
                        style={{ marginBottom: "0.4rem" }}
                        roomStyles={roomStyles}
                        key={`title-${item.type}`}
                      >
                        {item.type} ({`${item.list.length}`})
                      </ModerationSubTitle>
                      <Accordion spacing={0.5} allowMultiple>
                        {item.list.map((userObj, arrIndex, sortedList) => (
                          <User
                            userId={userObj.id}
                            key={`userId-${userObj.id}`}
                            isLast={arrIndex === sortedList.length - 1}
                            isModerator={isModerator}
                            isAudience={isAudience}
                          />
                        ))}
                      </Accordion>{" "}
                    </Box>
                  )}
                </React.Fragment>
              ))}
              {audienceList.length > 0 && (
                <Box marginBottom="0.6rem" key={`item.type`}>
                  <ModerationSubTitle
                    style={{ marginBottom: "0.4rem" }}
                    roomStyles={roomStyles}
                  >
                    <AudienceListHeader t={t} />
                  </ModerationSubTitle>
                  <Accordion spacing={0.5} allowMultiple ref={ref}>
                    <ViewportList
                      viewportRef={ref}
                      items={audienceList}
                      itemSize={42}
                    >
                      {(user, index) => (
                        <User
                          userId={user.id}
                          key={user.id}
                          isLast={false}
                          isAudience={isAudience}
                          isModerator={isModerator}
                        />
                      )}
                    </ViewportList>
                  </Accordion>
                </Box>
              )}
            </>
          ) : (
            <>
              {usersListsForAudienceUser.map(item => (
                <React.Fragment key={`userTypeOuter-${item.type}`}>
                  {item.list.length !== 0 && (
                    <Box key={`userType-${item.type}`} marginBottom="0.6rem">
                      <ModerationSubTitle
                        style={{ marginBottom: "0.4rem" }}
                        roomStyles={roomStyles}
                        key={`title-${item.type}`}
                      >
                        {item.type} ({`${item.list.length}`})
                      </ModerationSubTitle>
                      <Accordion spacing={0.5} allowMultiple>
                        {item.list.map((userObj, arrIndex, sortedList) => (
                          <User
                            userId={userObj.id}
                            key={`userId-${userObj.id}`}
                            isLast={arrIndex === sortedList.length - 1}
                            isModerator={isModerator}
                            isAudience={isAudience}
                          />
                        ))}
                      </Accordion>
                    </Box>
                  )}
                </React.Fragment>
              ))}
              {audienceList.length > 0 && (
                <Box marginBottom="0.6rem" key={`item.type`}>
                  <ModerationSubTitle
                    style={{ marginBottom: "0.4rem" }}
                    roomStyles={roomStyles}
                  >
                    <AudienceListHeader t={t} />
                  </ModerationSubTitle>
                  <Accordion
                    spacing={0.5}
                    ref={ref}
                    allowMultiple
                    marginBottom="0.6rem"
                  >
                    <ViewportList
                      viewportRef={ref}
                      items={audienceList}
                      itemSize={42}
                    >
                      {(user, index) => (
                        <User
                          userId={user.id}
                          key={user.id}
                          isLast={false}
                          isAudience={isAudience}
                          isModerator={isModerator}
                        />
                      )}
                    </ViewportList>
                  </Accordion>
                </Box>
              )}
            </>
          )}
        </Container>
      </Box>
    </Box>
  )
}

export const UserList = React.memo(_UserList)
